<template>
  <div class="onboarding-signup">
    <div v-if="$wait.is('Register')" class="onboarding-signup__loading-screen">
      <div class="onboarding-signup__loading-screen-inner">
        <p class="onboarding-signup__loading-text">Creating account</p>
        <ButtonSpinner />
      </div>
    </div>

    <div class="onboarding-signup__banner">
      <button
        v-if="isTemporaryUser"
        class="onboarding-signup__back-15807"
        @click="$router.back()"
      >
        <span class="iconfont iback"></span> Back
      </button>

      <div>
        <img
          class="onboarding-signup__logo"
          src="@/assets.app/images/logo.svg"
          alt="Shortform App"
        />
      </div>

      <Slider />
    </div>

    <div class="onboarding-signup__form--tablet">
      <BottomSheet ref="bottomSheetRef" innerClass="onboarding-signup__sheet">
        <SignupSheet :view="view" />
      </BottomSheet>
      <p class="onboarding-signup__links">
        By creating an account, you agree to the
        <router-link :to="{ name: 'site_terms' }">Terms of Service</router-link>
        and
        <router-link :to="{ name: 'site_privacy' }">Privacy Policy</router-link
        >.
      </p>

      <div class="onboarding-signup__primary-button-wrapper">
        <AppleSignInButton
          v-if="socialView?.appleAuthView && primaryButton === 'apple'"
          class="onboarding-signup__primary-button onboarding-signup__button--apple"
          :view="socialView.appleAuthView"
          title="Sign up with"
        />
        <GoogleSignInButton
          v-else-if="socialView?.googleAuthView && showGoogleSignupOnMobile"
          :authMode="authMode"
          :view="socialView.googleAuthView"
          class="onboarding-signup__primary-button onboarding-signup__primary-button--google"
          :class="{
            'onboarding-signup__primary-button--google-native': isGoogleNative,
          }"
          showButton="big"
        />
      </div>
      <div
        class="onboarding-signup__buttons"
        :class="{
          'onboarding-signup__buttons--small':
            primaryButton === 'google' && !isGoogleNative,
        }"
      >
        <AppleSignInButton
          v-if="
            socialView?.appleAuthView &&
            !isGoogleNative &&
            primaryButton !== 'apple'
          "
          class="onboarding-signup__button onboarding-signup__button--apple"
          :view="socialView.appleAuthView"
          title="Sign up with"
        />
        <GoogleSignInButton
          v-if="
            socialView?.googleAuthView &&
            showGoogleSignupOnMobile &&
            !isGoogleNative &&
            primaryButton !== 'google'
          "
          :authMode="authMode"
          :view="socialView.googleAuthView"
          class="onboarding-signup__button onboarding-signup__button--google"
          showButton="small"
        />

        <FacebookLoginButton
          v-if="socialView?.facebookAuthView"
          title=""
          :view="socialView.facebookAuthView"
          class="onboarding-signup__button onboarding-signup__button--facebook"
        >
        </FacebookLoginButton>

        <button
          class="onboarding-signup__button onboarding-signup__button--email"
          @click="openInfoSheet"
        >
          <img
            class="onboarding-signup__button-image"
            src="@/assets.app/images/onboarding/email.svg"
            alt=""
          />
        </button>
      </div>

      <div class="onboarding-signup__separator">
        <img src="@/assets.app/images/onboarding/separator-line.svg" alt="" />
      </div>

      <div class="onboarding-signup__login">
        Already have an account?
        <router-link :to="{ name: 'login', query: $route.query }"
          >Log in</router-link
        >
      </div>
    </div>

    <div v-if="view" class="sign-up onboarding-signup__form--desktop">
      <div class="sign-up__form-box">
        <div class="shortform-sign-up">
          <h2 class="shortform-sign-up__title">Sign Up to Shortform</h2>
          <form
            class="shortform-sign-up__form"
            role="form"
            novalidate
            @submit.prevent="submitForm"
          >
            <div class="shortform-sign-up__group">
              <label class="shortform-sign-up__label" for="register_full_name"
                >Full name</label
              >
              <InputWithContextMenu
                id="register_full_name"
                v-model="view.model.full_name"
                class="shortform-sign-up__input"
                :class="view.validation.stateClass('full_name')"
                type="text"
                placeholder=""
                autofocus
              />
              <FormError :errors="view.validation.errors" field="full_name" />
            </div>
            <div class="shortform-sign-up__group">
              <label class="shortform-sign-up__label" for="register_email"
                >Email Address</label
              >
              <InputWithContextMenu
                id="register_email"
                v-model="view.model.email"
                class="shortform-sign-up__input"
                :class="view.validation.stateClass('email')"
                type="email"
                placeholder=""
                autofocus
              />
              <FormError :errors="view.validation.errors" field="email" />
            </div>
            <div class="shortform-sign-up__group">
              <label class="shortform-sign-up__label" for="register_password"
                >Password</label
              >
              <InputWithContextMenu
                id="register_password"
                v-model="view.model.password"
                type="password"
                autocomplete="new-password"
                placeholder=""
                class="shortform-sign-up__input"
                :class="view.validation.stateClass('password')"
              />
              <FormError :errors="view.validation.errors" field="password" />
            </div>
            <div
              v-if="view.model.password_score"
              class="text-muted font-italic"
            >
              <small>
                password strength:
                <span
                  v-if="view.model.password_score === '4'"
                  class="text-success font-weight-700"
                  >strong</span
                >
                <span
                  v-if="view.model.password_score === '3'"
                  class="text-info font-weight-700"
                  >good</span
                >
                <span
                  v-if="view.model.password_score === '2'"
                  class="text-warning font-weight-700"
                  >medium</span
                >
                <span
                  v-if="view.model.password_score === '1'"
                  class="text-danger font-weight-700"
                  >weak</span
                >
                <span
                  v-if="view.model.password_score === '0'"
                  class="text-danger font-weight-700"
                  >terrible</span
                >
              </small>
            </div>
            <div
              v-if="view.validation.errors && view.validation.errors._schema"
              class="alert alert-danger"
            >
              {view.validation.errors._schema}
            </div>
            <div class="shortform-sign-up__group">
              <span class="shortform-sign-up__terms"
                >By creating an account, you agree to the
                <router-link
                  :to="{ name: 'site_terms' }"
                  class="black"
                  target="_blank"
                  >Terms of Service</router-link
                >
                and
                <router-link
                  :to="{ name: 'site_privacy' }"
                  class="black"
                  target="_blank"
                  >Privacy Policy</router-link
                >.
              </span>
            </div>
            <div class="shortform-sign-up__group">
              <v-wait for="Register" style="display: flex">
                <template v-slot:waiting>
                  <div class="shortform-sign-up__submit button button--theme">
                    Creating Account
                    <button-spinner />
                  </div>
                </template>
                <button
                  class="shortform-sign-up__submit button button--theme"
                  type="submit"
                >
                  Create Account
                </button>
              </v-wait>
            </div>
          </form>
          <SocialAuth
            :onStart="onStart"
            :onSuccess="onSuccess"
            :onError="onError"
            :authMode="authMode"
            :showGoogle="!showGoogleSignupOnMobile"
          />
          <div>
            <p class="shortform-sign-up__log-in">
              Already have an account?
              <router-link :to="loginRoute" style="color: #ffb600"
                ><strong>Log in</strong></router-link
              >
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import { SignupView } from '@/models/auth/models'
import { AuthMode, GoogleAuthView } from '@/models/auth/google'
import { auth } from '@/services/auth'
import {
  createSocialAuthView,
  SocialAuthComponentView,
} from '@/models/auth/social'
import { authCssFix } from '@/helpers/html'
import { assertNotNull } from '@/helpers/typing'
import { TABLET_WIDTH } from '@/init/settings'
import { platform } from '@/services/platform'
import { isAppleDevice } from '@/helpers/userAgent'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import SocialAuth from '@/app/components/auth/SocialAuth.vue'
import AppleSignInButton from '@/app/components/auth/AppleSignInButton.vue'
import FacebookLoginButton from '@/app/components/auth/FacebookLoginButton.vue'
import GoogleSignInButton from '@/app/components/auth/GoogleSignInButton.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import BottomSheet from '@/app/components/BottomSheet.vue'
import SignupSheet from '../signup/SignupSheet.vue'
import Slider from '../signup/Slider.vue'

const props = defineProps<{
  nextOnboardingPage: () => void
}>()

const route = useRoute()
const router = useRouter()
const wait = useWait()

const view = ref<SignupView | null>(null)
const socialView = ref<SocialAuthComponentView | null>(null)

const authMode = ref<AuthMode>(AuthMode.Signup)

const bottomSheetRef = ref<InstanceType<typeof BottomSheet> | null>(null)

const showGoogleSignupOnMobile = ref(false)
const primaryButton = ref<'apple' | 'google'>('google')
const isGoogleNative = ref(true)

function updateShowGoogleSignupOnMobile(): void {
  showGoogleSignupOnMobile.value = window.innerWidth <= TABLET_WIDTH

  if (
    socialView.value &&
    socialView.value.googleAuthView instanceof GoogleAuthView
  ) {
    const googleAuthView = socialView.value.googleAuthView as GoogleAuthView
    setTimeout(() => googleAuthView.renderButtons(), 100)
  }
}

function onStart(): void {
  wait.start('Register')
}
function openInfoSheet(): void {
  bottomSheetRef.value?.view.open()
}

async function onSuccess(nextPage: any): Promise<void> {
  wait.start('Register')

  // Timeout before the redirect to let Mixpanel calls complete.
  // It is less problematic in the main app, where Mixpanel would track
  // missing events on the next page, but in the extension, the next page
  // is the extension auth page, which closes immediately and Mixpanel
  // calls are lost, so we set a longer timeout in this case.
  // See #9509.
  let timeout = 100
  // Redirect to extension auth page if we need to
  if (router.currentRoute.value.query?.redirect === '/extension-auth') {
    nextPage = { name: 'extension_auth' }
    timeout = 3000
  }

  if (nextPage) {
    setTimeout(() => {
      router.push(nextPage)
      wait.end('Register')
    }, timeout)
  } else {
    setTimeout(() => {
      props.nextOnboardingPage()
      wait.end('Register')
    }, timeout)
  }
}

function onError(): void {
  wait.end('Register')
}

onBeforeMount(async () => {
  view.value = new SignupView(onStart, onSuccess, onError)

  socialView.value = await createSocialAuthView(onStart, onSuccess, onError)
  if (socialView.value) {
    socialView.value.init()
  }

  // We only show the apple button on apple devices (browser or native app)

  if (isAppleDevice() || (await platform.isNativeIOSApp())) {
    primaryButton.value = 'apple'
  }

  isGoogleNative.value = await platform.isNativeAndroidApp()

  updateShowGoogleSignupOnMobile()
  window.addEventListener('resize', updateShowGoogleSignupOnMobile)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateShowGoogleSignupOnMobile)
})

const _view = computed(() => {
  return assertNotNull(view.value)
})

onMounted(async () => {
  // If user name was passed a prop (from the signup with email form)
  if (route.query.email) {
    _view.value.model.email = route.query.email as string
  }

  authCssFix()
})

async function submitForm(): Promise<void> {
  await _view.value.start()
}

const loginRoute = computed(() => {
  const routeData = { name: 'login', query: route.query }
  // If we have the email set, pass it on to the signup page.
  if (_view.value.model.email) {
    routeData.query.email = _view.value.model.email
  }
  return routeData
})

const isTemporaryUser = computed(() => {
  return auth.isTemporaryUser()
})
</script>

<style lang="scss">
.onboarding-signup {
  &__sheet {
    background: $c-bg !important;
    padding: 0 !important;

    &::before {
      display: none;
    }
  }
}
</style>

<style lang="scss" scoped>
.onboarding-signup {
  width: 100%;
  height: 100%;
  display: flex;
  background: $c-white;

  @media only screen and (max-width: $w-tablet-s) {
    flex-direction: column;
  }

  &__banner {
    display: flex;
    padding: 22px;
    padding-top: 56px;
    background: $c-bg;
    flex-direction: column;

    align-items: center;
    justify-content: space-between;
    width: 560px;
    height: 100%;
    @supports (padding-top: env(safe-area-inset-top)) {
      padding-top: max(56px, env(safe-area-inset-top));
    }

    @media only screen and (max-width: $w-tablet-s) {
      width: 100%;
      min-height: 0;
      align-items: inherit;
      border-bottom-left-radius: 20px;
      border-bottom-right-radius: 20px;

      @media only screen and (max-height: 811px) {
        padding-top: 22px;
        @supports (padding-top: env(safe-area-inset-top)) {
          padding-top: max(22px, env(safe-area-inset-top));
        }
      }

      @media only screen and (max-height: 714px) {
        padding-bottom: 12px;
      }
    }
  }

  &__back-15807 {
    width: fit-content;
    margin-bottom: 8px;
    font-size: 16px;
    color: $c-black-3;
  }

  &__form {
    &--tablet {
      display: none;
      @media only screen and (max-width: $w-tablet-s) {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;
        padding: var(--onboarding-padding);

        @media only screen and (max-height: 714px) {
          padding-top: 12px;
        }
      }
    }

    &--desktop {
      display: block;
      flex-grow: 1;

      @media only screen and (max-width: $w-tablet-s) {
        display: none;
      }
    }
  }

  &__links {
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    max-width: 280px;
    margin-inline: auto;
    color: $c-black-6;
    margin-top: 32px;

    @media only screen and (max-width: $w-tablet-s) {
      margin: 0;
      padding-inline: 12px;
    }
    @media only screen and (max-width: $w-medium-s) {
      padding-inline: 0px;
    }

    a {
      color: $c-black-3;
    }
  }

  &__primary-button-wrapper {
    display: flex;
    justify-content: center;

    @media only screen and (max-width: $w-tablet-s) {
      max-width: var(--onboarding-inner-width);
      width: 100%;
    }
  }

  &__primary-button {
    padding: 16px 40px;
    margin-inline: 24px;
    border-radius: 100px;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: -0.02em;
    margin-top: 20px;
    display: flex;
    gap: 6px;
    justify-content: center;
    align-items: start;
    background-color: $c-black-1;

    @media only screen and (max-width: $w-tablet-s) {
      width: 100%;
      margin: 0;
      padding-inline: 0;
    }

    &--google {
      padding: 0;
      width: auto;
      background-color: transparent;
      :deep(> div) {
        width: 100%;
        display: flex;
        align-items: center;
      }
      :deep(.shortform-sign-up__button) {
        gap: 6px;
        display: flex;
        align-items: center;
        padding-block: 16px;
        flex-grow: 1;
        justify-content: center;
        color: white;
      }
    }
    &--google-native {
      width: 100%;
      background-color: $c-black-1;
    }
  }

  &__separator {
    display: flex;
    justify-content: center;
  }

  &__buttons {
    margin-top: 24px;
    gap: 24px;
    display: flex;
    justify-content: center;
    align-items: center;

    margin-inline: 24px;

    :deep(#sign-in-with-google-container-small) {
      transform: scale(1.3);
      transform-origin: center;
    }

    @media only screen and (max-width: $w-tablet-s) {
      margin: 0;

      :deep(.button__text) {
        display: none !important;
      }
    }
  }

  &__button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 56px;
    height: 56px;
    border: 1px solid $c-border-3;
    border-radius: 50%;
    filter: drop-shadow(0px 1px 5px rgba(255, 255, 255, 0.1))
      drop-shadow(0px 8px 15px rgba(20, 28, 49, 0.1));
    background: $c-white;

    &--apple {
      background-color: $c-black-1;
      color: $c-white;
    }
  }

  &__button--google {
    @media only screen and (max-width: $w-tablet-s) {
      border-color: transparent;
      position: relative;
      &:after {
        pointer-events: none;
        content: '';
        position: absolute;
        top: -1px;
        left: -1px;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        border: solid 1px $c-border-3;
      }

      :deep(.googleSigninSmall) {
        position: relative;
        top: -2px;
        width: 40px;
        height: 40px;
        transform: scale(1.4) !important;

        &:after {
          pointer-events: none;
          content: '';
          position: absolute;
          top: 2px;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: solid 2px $c-white;
        }
      }
      :deep(.button__img) {
        width: 25px;
      }
    }
  }

  &__button--facebook {
    border-color: transparent;
    background: #3b5998;

    :deep(.button__img) {
      @media only screen and (max-width: $w-tablet-s) {
        width: 14px;
      }
    }
  }

  &__buttons--small .onboarding-signup {
    &__button {
      width: 48px;
      height: 48px;
    }

    &__button--facebook :deep(.button__img) {
      @media only screen and (max-width: $w-tablet-s) {
        width: 12px;
      }
    }

    &__button-image {
      width: 26px;
    }
  }

  &__login {
    font-size: 16px;
    line-height: 23px;
    text-align: center;
    color: $c-black-15;
    margin-top: 16px;
    a {
      color: $c-yellow;
    }
    @media only screen and (max-width: $w-tablet-s) {
      margin: 0;
    }
  }

  .sign-up {
    .shortform-sign-up {
      &__title {
        margin-top: 64px;
        margin-bottom: 32px;
      }

      &__submit {
        margin-top: 24px;
      }

      &__partition {
        margin-top: 24px;
        margin-bottom: 24px;
      }

      &__log-in {
        margin-top: 32px;
      }
    }

    .sign-up__form-box {
      width: auto;
      margin-top: 64px;
    }
  }

  &__loading-screen {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 999;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    animation: fadeIn 0.5s ease;
    @media only screen and (min-width: $w-tablet-b) {
      display: none;
    }
  }

  &__loading-screen-inner {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 16px 32px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.8);
  }

  &__loading-text {
    color: $c-white;
    margin: 0;
  }
}

:deep(.shortform-sign-up__partition) {
  z-index: 2;
}
</style>
